import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, isColorDark, resolveLinkProps, resolveImage } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const QuoteIcon = ({ color }) => (
    <svg className="w-8 h-8 opacity-30" fill={color} viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z"/></svg>
);

const ArrowIcon = () => (
    <svg className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
);

export default function AdvisorCtaSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'card';

    const advisorImg = resolveImage(content, 'advisor_image', mediaFolders);
    const dark = isColorDark(s.accent);
    const btnText = dark ? '#ffffff' : '#1f2937';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const Photo = ({ size = 'w-24 h-24', ring = true }) => (
        <div className={`${size} rounded-full overflow-hidden flex-shrink-0 ${ring ? 'ring-4' : ''}`} style={ring ? { ringColor: hexToRgba(s.accent, 0.3) } : {}}>
            {advisorImg ? (
                <img src={advisorImg} alt={content.advisor_name || ''} className="w-full h-full object-cover" loading="lazy"/>
            ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: s.accent }}>
                    {(content.advisor_name || 'A').charAt(0)}
                </div>
            )}
        </div>
    );

    const AdvisorInfo = ({ nameClass = 'text-lg', roleClass = 'text-sm' }) => (
        <div>
            {content.advisor_name && <p className={`${nameClass} font-bold`} style={{ color: s.text }}>{content.advisor_name}</p>}
            {content.advisor_role && <p className={`${roleClass}`} style={{ color: s.accent }}>{content.advisor_role}</p>}
        </div>
    );

    const Buttons = () => (
        <div className="flex flex-wrap gap-3">
            {content.cta_text && (
                <a {...resolveLinkProps(content.cta_link || '#')} className="group inline-flex items-center px-6 py-3 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    style={{ backgroundColor: s.accent, color: btnText }}>
                    {content.cta_text}<ArrowIcon/>
                </a>
            )}
            {content.secondary_cta_text && (
                <a {...resolveLinkProps(content.secondary_cta_link || '#')} className="inline-flex items-center px-6 py-3 rounded-full font-semibold text-sm border-2 hover:scale-105 transition-all"
                    style={{ borderColor: s.accent, color: s.accent }}>
                    {content.secondary_cta_text}
                </a>
            )}
        </div>
    );

    const Testimonial = () => {
        if (!content.testimonial_text) return null;
        return (
            <div className={`mt-6 pt-6 border-t transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', transitionDelay: visible ? '400ms' : '0ms' }}>
                <QuoteIcon color={s.accent}/>
                <p className="text-sm italic mt-2" style={{ color: s.textSub }}>{content.testimonial_text}</p>
                {content.testimonial_name && (
                    <p className="text-xs font-semibold mt-2" style={{ color: s.text }}>
                        {content.testimonial_name}{content.testimonial_role && <span className="font-normal" style={{ color: s.textSub }}> — {content.testimonial_role}</span>}
                    </p>
                )}
            </div>
        );
    };

    // ── Split variant ──
    if (variant === 'split') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div className={`flex justify-center transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                            <div className="relative">
                                <div className="absolute -inset-4 rounded-3xl opacity-20" style={{ background: `linear-gradient(135deg, ${s.accent}, ${s.secondary})` }}/>
                                <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl">
                                    {advisorImg ? (
                                        <img src={advisorImg} alt={content.advisor_name || ''} className="w-full h-full object-cover" loading="lazy"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-6xl font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {(content.advisor_name || 'A').charAt(0)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: visible ? '200ms' : '0ms' }}>
                            <AdvisorInfo nameClass="text-xl" roleClass="text-sm"/>
                            {content.message && <p className="text-base mt-4 leading-relaxed" style={{ color: s.textSub }}>{content.message}</p>}
                            <div className="mt-6"><Buttons/></div>
                            <Testimonial/>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Minimal variant ──
    if (variant === 'minimal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="max-w-lg mx-auto text-center">
                        <div className={`flex justify-center mb-4 transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                            <Photo size="w-20 h-20"/>
                        </div>
                        <AdvisorInfo nameClass="text-lg" roleClass="text-sm"/>
                        {content.message && <p className="text-sm mt-3 leading-relaxed" style={{ color: s.textSub }}>{content.message}</p>}
                        <div className="mt-5 flex justify-center"><Buttons/></div>
                        <Testimonial/>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Floating variant ──
    if (variant === 'floating') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className="max-w-xl mx-auto">
                        <div className={`relative rounded-2xl shadow-2xl pt-16 pb-8 px-8 text-center transition-all duration-500 hover:shadow-3xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff', border: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}` }}>
                            <div className="absolute -top-10 left-1/2 -translate-x-1/2">
                                <div className="w-20 h-20 rounded-full overflow-hidden ring-4 shadow-lg" style={{ ringColor: s.bg || '#ffffff' }}>
                                    {advisorImg ? (
                                        <img src={advisorImg} alt={content.advisor_name || ''} className="w-full h-full object-cover" loading="lazy"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: s.accent }}>
                                            {(content.advisor_name || 'A').charAt(0)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <AdvisorInfo/>
                            {content.message && <p className="text-sm mt-3 leading-relaxed" style={{ color: s.textSub }}>{content.message}</p>}
                            <div className="mt-5 flex justify-center"><Buttons/></div>
                            <Testimonial/>
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Card variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                <div className={`max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-xl transition-all duration-500 hover:shadow-2xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                    style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.05)}, ${hexToRgba(s.secondary, 0.08)})`, border: `1px solid ${s.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
                    <div className="flex flex-col md:flex-row items-center gap-6 p-8">
                        <Photo size="w-28 h-28"/>
                        <div className="flex-1 text-center md:text-left">
                            <AdvisorInfo nameClass="text-xl"/>
                            {content.message && <p className="text-sm mt-3 leading-relaxed" style={{ color: s.textSub }}>{content.message}</p>}
                            <div className="mt-5"><Buttons/></div>
                        </div>
                    </div>
                    <Testimonial/>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
        </section>
    );
}
